/**
 * Boot Health Check - Runtime guardrail to catch auth API miswiring
 * Prevents silent failures when Pages Functions intercept auth routes
 */

import { API_CONFIG } from '../config/api';

const CHECK_TIMEOUT = 5000; // 5 seconds

let healthCheckResult = null;

/**
 * Check if auth API is properly wired (returns JSON, not HTML)
 */
export async function checkAuthAPIHealth() {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('[BootCheck] Verifying auth API health...');
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT);
    
    const response = await fetch(API_CONFIG.AUTH.HEALTH, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`Auth API returned ${response.status}`);
    }
    
    const contentType = response.headers.get('Content-Type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error(`Auth API returned ${contentType}, expected JSON`);
    }
    
    const data = await response.json();
    
    if (!data.status || data.status !== 'ok') {
      throw new Error(`Auth API unhealthy: ${JSON.stringify(data)}`);
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[BootCheck] ✅ Auth API healthy');
    }
    healthCheckResult = { healthy: true, message: 'Auth API responding correctly' };
    return healthCheckResult;
    
  } catch (error) {
    const errorMsg = error.name === 'AbortError' 
      ? 'Auth API timeout' 
      : `Auth API error: ${error.message}`;
    
    console.error('[BootCheck] ❌', errorMsg);
    healthCheckResult = { 
      healthy: false, 
      message: errorMsg,
      suggestion: 'Check if Pages Functions are intercepting /auth routes'
    };
    return healthCheckResult;
  }
}

/**
 * Get the cached health check result
 */
export function getAuthHealthStatus() {
  return healthCheckResult;
}

/**
 * Show red banner if auth API is miswired
 */
export function showHealthWarningIfNeeded() {
  if (healthCheckResult && !healthCheckResult.healthy) {
    // Create warning banner
    const banner = document.createElement('div');
    banner.id = 'auth-health-warning';
    banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #dc2626;
      color: white;
      padding: 8px;
      text-align: center;
      font-weight: bold;
      z-index: 9999;
      font-size: 14px;
    `;
    banner.innerHTML = `
      ⚠️ AUTH API MISWIRED: ${healthCheckResult.message}
      <button onclick="this.parentElement.remove()" style="margin-left: 10px; background: white; color: #dc2626; border: none; padding: 2px 6px; cursor: pointer;">×</button>
    `;
    
    // Add to page
    if (document.body) {
      document.body.prepend(banner);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.prepend(banner);
      });
    }
  }
}